import { api } from './api';
import { CustomHLDBrief, DiagramState, ReviewedHLDAttempt, WorkspaceSession } from '../types/hld';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Payload accepted by the autosave upsert (`PUT /hld/sessions/:problemId`).
 * All fields optional so autosave can persist partial state idempotently.
 * Mirrors the backend `UpsertHLDSessionDto` (Req 12.2, 12.4, 12.5).
 */
export interface SaveHLDSessionInput {
 requirements?: { functional?: string[]; nonFunctional?: string[] };
 estimations?: {
  dau?: number;
  qps?: number;
  storage?: number;
  bandwidth?: number;
  cacheSize?: number;
 };
 apiDesign?: string;
 dataModel?: string;
 diagramJson?: DiagramState;
 tradeoffs?: { choice: string; justification: string }[];
 score?: number | null;
 isCustom?: boolean;
 customBrief?: CustomHLDBrief;
 reviewedAttempts?: ReviewedHLDAttempt[];
}

const BASE = '/hld/sessions';

// ─── Service ─────────────────────────────────────────────────────────────────

class HLDSessionService {
 /**
  * Get the current user's session for a problem (Req 12.5). Owner-scoped on the
  * server via the Bearer token; a non-owned session cannot match.
  */
 async getSession(problemId: string): Promise<WorkspaceSession> {
  return await api.get(`${BASE}/${encodeURIComponent(problemId)}`);
 }

 /**
  * Create-or-update (upsert) the current user's session for a problem. Used by
  * autosave; idempotent and keyed by `(userId, problemId)` (Req 12.2, 12.5).
  */
 async saveSession(
  problemId: string,
  payload: SaveHLDSessionInput,
 ): Promise<WorkspaceSession> {
  return await api.put(`${BASE}/${encodeURIComponent(problemId)}`, payload);
 }

 async listCustomWorkspaces(): Promise<WorkspaceSession[]> {
  return await api.get(`${BASE}/custom`);
 }

 async createShare(problemId: string): Promise<{ token: string }> {
  return await api.post(`${BASE}/${encodeURIComponent(problemId)}/share`);
 }

 async getSharedSnapshot(token: string): Promise<WorkspaceSession> {
  return await api.get(`/hld/shared/${encodeURIComponent(token)}`);
 }
}

export const hldSessionService = new HLDSessionService();
export default hldSessionService;
